// services/passViewer.tsx
import { useState } from "react";
import { Form, InputGroup, Button } from "react-bootstrap";
import { Eye, EyeSlash } from "react-bootstrap-icons";

type PasswordFieldProps = {
  id: string;
  label: string;
  name: string;
  value: string;
  placeholder?: string;
  required?: boolean;
  isInvalid?: boolean; // <- drive visibility from parent
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  feedbackInvalid?: string;
};

export function PasswordField({
  id,
  label,
  name,
  value,
  placeholder = "",
  required,
  isInvalid = false,
  onChange,
  onBlur,
  feedbackInvalid,
}: PasswordFieldProps) {
  const [reveal, setReveal] = useState(false);

  const hold = {
    onMouseDown: (e: React.MouseEvent) => { e.preventDefault(); setReveal(true); },
    onMouseUp: () => setReveal(false),
    onMouseLeave: () => setReveal(false),
    onTouchStart: () => setReveal(true),
    onTouchEnd: () => setReveal(false),
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === " " || e.key === "Enter") setReveal(true); },
    onKeyUp: () => setReveal(false),
  };

  return (
    <Form.Group className="mb-3" controlId={id}>
      <Form.Label>{label}</Form.Label>

      {/* Keep feedback OUTSIDE the InputGroup; enable validation on the group */}
      <InputGroup hasValidation>
        <Form.Control
          type={reveal ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          isInvalid={isInvalid}     // this triggers the invalid state
          autoComplete={name === "password" ? "new-password" : "off"}
        />
        <Button
          {...hold}
          variant="outline-secondary"
          type="button"
          aria-label={reveal ? "Hide password" : "Show password"}
          title="Press and hold to show"
        >
          {reveal ? <EyeSlash /> : <Eye />}
        </Button>
      </InputGroup>

      {/* Render feedback BELOW the InputGroup so layout doesn't break */}
      {isInvalid && (
        <div className="invalid-feedback d-block">
          {feedbackInvalid}
        </div>
      )}
    </Form.Group>
  );
}
